/**
 * Responsibility: show a card image at full size without leaving the page.
 *
 * Opening the CDN URL in a new tab technically worked and was the wrong
 * shape: it dumps a raw image onto a blank page, loses the card's context,
 * and leaves a tab to close before getting back to the board. Judging
 * centring is a two-second glance, so it belongs in an overlay you dismiss
 * with Escape.
 *
 * Sized to the viewport rather than the image, because a hi-res scan is
 * around 1500px tall and would otherwise overflow on a laptop - which would
 * reintroduce the scrolling this is meant to avoid.
 */
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

type ImageLightboxProps = {
  src: string | null;
  alt: string;
  onClose: () => void;
};

export function ImageLightbox({ src, alt, onClose }: ImageLightboxProps) {
  if (!src) return null;

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{
        // Above the dialog it was opened from.
        zIndex: (theme) => theme.zIndex.modal + 1,
        bgcolor: 'rgba(0, 0, 0, 0.85)',
        p: { xs: 2, md: 4 },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close image"
        sx={{ position: 'absolute', top: 16, right: 16, color: 'common.white' }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        component="img"
        src={src}
        alt={alt}
        // Stops a click on the picture itself from dismissing, which is the
        // reflex when zooming in on a corner.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          borderRadius: 1,
          boxShadow: 24,
        }}
      />
    </Backdrop>
  );
}
